(ns hai.notes.detail
  "Full-page view of a single note as the shared editor surface — the same
   one the edit/create modals show: auto-saving text, tag/link/contact
   sections, the save/share footer, and the header ⋮ holding Delete."
  (:require
   [clojure.string :as str]
   [hai.sharing.helper :as sharing]
   [hai.notes.data :as data]
   [hai.notes.editor :as editor]
   [hai.ui.components :as c]
   [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [hai.web-app.viewport :as viewport]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(defn- note-label
  "First non-empty line of a note, truncated. Falls back to \"Note\" for an
   empty or not-yet-loaded note."
  [note]
  (let [first-line (some->> (:note/text note)
                            str/split-lines
                            (map str/trim)
                            (some not-empty))]
    (cond
      (nil? first-line)         "Note"
      (> (count first-line) 60) (str (subs first-line 0 60) "…")
      :else                     first-line)))

(defsub ::window-title
  [db]
  (let [note-uuid (get-in db [:routing/current-route :path-params :note/uuid])
        note (when note-uuid (get-in db [:note/uuid (uuid (str note-uuid))]))]
    (note-label note)))

;; Tracks which notes a resolve has completed for, so the page can tell
;; "still loading" apart from "not found / no access" — both leave a nil entity.
(defevent ::mark-resolved [db note-uuid]
  (update db ::resolved-uuids (fnil conj #{}) (uuid (str note-uuid))))

(defsub ::resolved?
  [db note-uuid]
  (boolean (and note-uuid
                (contains? (get db ::resolved-uuids #{}) (uuid (str note-uuid))))))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

;; The dialog caps the modal surface at 900px and floats it over a dimmed
;; page; here the column mirrors the cap and a shadow provides the elevation
;; the overlay would.
(o/defstyled editor-column :div
  {:max-width "900px"}
  [editor/edit-modal
   {:box-shadow "0 1px 2px rgba(45,62,42,0.08), 0 12px 32px rgba(45,62,42,0.16)"}])

(o/defstyled muted :span
  {:color t/--card-forest-70 :font-size "14px"})

(o/defstyled loading-state :div
  :flex :items-center
  {:gap "10px" :padding "8px 2px" :color t/--card-forest-70})

;; ---------------------------------------------------------------------------
;; View
;; ---------------------------------------------------------------------------

(defn- note-view [note]
  (r/with-let [!autosave (atom nil)]
    (let [note-uuid         (:note/uuid note)
          can-edit?         (sharing/can-edit? note)
          can-delete?       (sharing/can-delete? note)
          removing-links    @(<s [::data/removing-links note-uuid])
          removing-tags     @(<s [::data/removing-tags note-uuid])
          removing-contacts @(<s [::data/removing-contacts note-uuid])
          removing-notes    @(<s [::data/removing-notes note-uuid])
          removing-tasks    @(<s [::data/removing-tasks note-uuid])
          link-urls      (->> (:note/activities note)
                              (keep :activity/url)
                              (remove removing-links)
                              vec)
          tags           (->> (:note/tags note)
                              (remove #(contains? removing-tags (:group/uuid %)))
                              vec)
          contacts       (->> (:note/contacts note)
                              (remove #(contains? removing-contacts (:contact/uuid %)))
                              vec)
          linked-notes   (->> (:note/notes note)
                              (remove #(contains? removing-notes (:note/uuid %)))
                              vec)
          linked-tasks   (->> (:note/tasks note)
                              (remove #(contains? removing-tasks (:task/uuid %)))
                              vec)
          delete! (fn []
                    ;; Drop the pending autosave so the unmount flush can't
                    ;; re-save the note being deleted.
                    (when-let [{:keys [discard!]} @!autosave]
                      (discard!))
                    (>e [::data/delete note-uuid])
                    (>e [:routing/navigate [:notes/view]]))
          mobile? @(<s [::viewport/mobile?])
          editor-props
          {:title           (if can-edit? "Editing note" "Note")
           :note            note
           :tags            tags
           :link-urls       link-urls
           :contacts        contacts
           :linked-notes    linked-notes
           :linked-tasks    linked-tasks
           :editing?        can-edit?
           :share?          can-edit?
           :auto-focus?     false
           :form-id         [::edit-form note-uuid]
           :!autosave       !autosave
           :on-save-text    (fn [text]
                              (>e [::data/save {:note/uuid note-uuid :note/text text}]))
           :extras-handlers
           {:on-add-tag        (fn [tag]
                                 (if-let [tag-uuid (:group/uuid tag)]
                                   (>e [::data/add-tag {:note/uuid note-uuid :tag-uuid tag-uuid}])
                                   (>e [::data/add-tag {:note/uuid note-uuid :tag-name (:tag/name tag)}])))
            :on-remove-tag     (fn [tag] (>e [::data/remove-tag {:note/uuid note-uuid :group/uuid (:group/uuid tag)}]))
            :on-add-link       (fn [link-url] (>e [::data/add-link {:note/uuid note-uuid :url link-url}]))
            :on-remove-link    (fn [link-url] (>e [::data/remove-link {:note/uuid note-uuid :url link-url}]))
            :on-add-contact    (fn [contact-uuid] (>e [::data/add-contact {:note/uuid note-uuid :contact/uuid contact-uuid}]))
            :on-remove-contact (fn [contact-uuid] (>e [::data/remove-contact {:note/uuid note-uuid :contact/uuid contact-uuid}]))
            :on-add-note       (fn [linked-uuid] (>e [::data/add-note-link {:note/uuid note-uuid :note/linked-uuid linked-uuid}]))
            :on-remove-note    (fn [linked-uuid] (>e [::data/remove-note-link {:note/uuid note-uuid :note/linked-uuid linked-uuid}]))
            :on-add-task       (fn [task-uuid] (>e [::data/add-task {:note/uuid note-uuid :task/uuid task-uuid}]))
            :on-remove-task    (fn [task-uuid] (>e [::data/remove-task {:note/uuid note-uuid :task/uuid task-uuid}]))
            :on-open-entity    (fn [entity] (>e [::data/open-entity entity]))}
           :on-share-closed #(>e [:entity/resolve {:note/uuid note-uuid}])
           :on-close        #(>e [:routing/navigate [:notes/view]])
           :on-delete       (when can-delete? delete!)}]
      (if mobile?
        ;; The same full-screen surface the home card expands into; ✕ returns
        ;; to the wall.
        [editor/screen editor-props]
        [editor-column
         (when-not can-edit?
           [:span [:span {:style {:color "var(--card-warning-text)" :font-style "italic"}} "This note is read-only"]])
         [editor/modal-content editor-props]]))))

(defn page [_route]
  (let [params    @(<s [:routing/path-params])
        note      @(<s [:entity params])
        resolved? @(<s [::resolved? (:note/uuid params)])]
    [layout/page
     [layout/content
      (cond
        note      ^{:key (:note/uuid note)} [note-view note]
        resolved? [muted "This note doesn't exist or you no longer have access to it."]
        :else     [loading-state [c/Spinner {:size "3"}] "Loading…"])]]))
